import Phaser from 'phaser';
import {
  TILE_SIZE,
  BASE_HP,
  BASE_UPGRADE_HP,
  ENEMY_DAMAGE_MULTIPLIER,
  ActorType,
  BaseUpgradeAxis,
  BASE_UPGRADE_DEFS,
  BaseUpgradeAxisDef,
} from '@/config/GameConfig';
import { EventBus, GameEvents } from '@/events/EventBus';
import { SoundManager } from '@/systems/SoundManager';
import { GOD } from '@/config/GodMode';
import { Actor, TurretTarget, BASE_TURRET_RANGE } from '@/entities/Actor';

// ── Shared target interface (compatible with EnemyProjectileManager.RobotTarget) ──

export interface IBaseTarget {
  x: number;
  y: number;
  alive: boolean;
  takeDamage(amount: number): void;
  collisionRadius: number;
  // Safety stubs — bases don't support these but projectiles may call them
  stun(duration: number): void;
  knockBack(maxTiles: number, proximity?: number): void;
  applyBurn(duration: number, dps: number): void;
  getVelocity(): { vx: number; vy: number };
}

// ══════════════════════════════════════════════════════════════
//  PlayerBase — manages one or more Actor nodes. Game over only
//  when ALL nodes are destroyed.
//  Multi-axis upgrade system: HP, Armor, Range, Cannons, Firepower
// ══════════════════════════════════════════════════════════════

export class PlayerBase implements IBaseTarget {
  /** Unique names for each base node when there are multiple */
  private static readonly BASE_NAMES = ['Alpha', 'Beta', 'Gamma', 'Delta', 'Epsilon', 'Zeta'];

  /** Centroid of alive nodes (updated on damage) */
  public x: number;
  public y: number;
  public collisionRadius: number;

  /** Per-axis upgrade levels */
  private axisLevels: Map<BaseUpgradeAxis, number>;

  /** Internal base actors — one per base position */
  private nodes: Actor[] = [];

  /** Aggregated HP across all nodes */
  get hp(): number { return this.nodes.reduce((s, n) => s + n.hp, 0); }
  get maxHp(): number { return this.nodes.reduce((s, n) => s + n.maxHp, 0); }
  get alive(): boolean { return this.nodes.some(n => n.alive); }

  /** Total number of base nodes */
  get totalNodes(): number { return this.nodes.length; }
  /** Number of destroyed base nodes */
  get destroyedNodes(): number { return this.nodes.filter(n => !n.alive).length; }
  /** Whether this map has multiple base nodes */
  get isMultiBase(): boolean { return this.nodes.length > 1; }

  /** Get per-node info (label, hp, alive) for UI display */
  getNodeInfos(): { label: string; hp: number; maxHp: number; alive: boolean; x: number; y: number }[] {
    return this.nodes.map(n => ({
      label: n.label, hp: n.hp, maxHp: n.maxHp, alive: n.alive, x: n.x, y: n.y,
    }));
  }

  /** Get label of the nearest alive node to a position */
  nearestAliveLabel(fromX: number, fromY: number): string {
    const node = this.nearestAliveNode(fromX, fromY);
    return node?.label ?? '';
  }

  // ── Derived stats from upgrade levels ──

  /** Legacy compat: HP axis level */
  get upgradeLevel(): number { return this.getAxisLevel(BaseUpgradeAxis.HP); }

  /** Armor damage reduction (0..0.5), 5% per level, capped at 50% */
  get armorReduction(): number {
    const level = this.getAxisLevel(BaseUpgradeAxis.ARMOR);
    return Math.min(0.5, level * 0.05);
  }

  /** Extra turret range in tiles (0.5 per level) */
  get bonusRangeTiles(): number {
    return this.getAxisLevel(BaseUpgradeAxis.RANGE) * 0.5;
  }

  /** Number of extra cannons (1 per level, base has 1 already) */
  get totalCannons(): number {
    return 1 + this.getAxisLevel(BaseUpgradeAxis.CANNONS);
  }

  /** Extra damage per projectile (2 per level) */
  get bonusDamage(): number {
    return this.getAxisLevel(BaseUpgradeAxis.FIREPOWER) * 2;
  }

  /** Effective turret range in pixels (base + upgrades + GodMode mult) */
  get turretRange(): number {
    return (BASE_TURRET_RANGE + this.bonusRangeTiles * TILE_SIZE) * GOD.baseTurretRangeMult;
  }

  constructor(
    private scene: Phaser.Scene,
    positions: { x: number; y: number }[],
    upgradeLevels: Record<string, number>,
  ) {
    this.collisionRadius = TILE_SIZE / 2;
    // Initialize axis levels from saved state
    this.axisLevels = new Map();
    for (const [axis, level] of Object.entries(upgradeLevels)) {
      this.axisLevels.set(axis as BaseUpgradeAxis, level);
    }
    const nodeHp = BASE_HP + this.getAxisLevel(BaseUpgradeAxis.HP) * BASE_UPGRADE_HP;
    const multiBase = positions.length > 1;
    for (let i = 0; i < positions.length; i++) {
      const pos = positions[i];
      const col = Math.floor(pos.x / TILE_SIZE);
      const row = Math.floor(pos.y / TILE_SIZE);
      this.nodes.push(new Actor(scene, col, row, ActorType.PLAYER_BASE, {
        hp: nodeHp,
        textureKey: 'actor_player_base',
        hasTurret: true,
        label: multiBase ? PlayerBase.BASE_NAMES[i] ?? `Base ${i + 1}` : undefined,
      }));
    }
    this.x = positions.reduce((s, p) => s + p.x, 0) / positions.length;
    this.y = positions.reduce((s, p) => s + p.y, 0) / positions.length;
  }

  // ── Axis level access ──

  getAxisLevel(axis: BaseUpgradeAxis): number {
    return this.axisLevels.get(axis) ?? 0;
  }

  getAxisDef(axis: BaseUpgradeAxis): BaseUpgradeAxisDef {
    return BASE_UPGRADE_DEFS.find(d => d.axis === axis)!;
  }

  /** Get resource cost for upgrading a specific axis to the next level */
  getAxisUpgradeCost(axis: BaseUpgradeAxis): number {
    const def = this.getAxisDef(axis);
    const level = this.getAxisLevel(axis);
    return Math.round(def.baseCost * Math.pow(def.costGrowth, level) * GOD.baseUpgradeCostMult);
  }

  /** Get optional campaign currency cost for upgrading a specific axis */
  getAxisCurrencyCost(axis: BaseUpgradeAxis): number {
    const def = this.getAxisDef(axis);
    if (!def.currencyCost) return 0;
    const level = this.getAxisLevel(axis);
    return Math.round(def.currencyCost * Math.pow(def.costGrowth, level));
  }

  /** Whether this axis can still be upgraded */
  canUpgradeAxis(axis: BaseUpgradeAxis): boolean {
    const def = this.getAxisDef(axis);
    return this.getAxisLevel(axis) < def.maxLevel;
  }

  /** Perform upgrade on a specific axis */
  upgradeAxis(axis: BaseUpgradeAxis): void {
    const def = this.getAxisDef(axis);
    const currentLevel = this.getAxisLevel(axis);
    if (currentLevel >= def.maxLevel) return;
    this.axisLevels.set(axis, currentLevel + 1);

    // Apply immediate effects
    if (axis === BaseUpgradeAxis.HP) {
      for (const n of this.nodes) {
        if (n.alive) n.addHp(BASE_UPGRADE_HP);
      }
    }

    EventBus.emit(GameEvents.BASE_UPGRADED, {
      axis,
      level: currentLevel + 1,
      hp: this.hp,
      maxHp: this.maxHp,
    });
  }

  /** Get all axis levels as a record (for persistence) */
  getAllLevels(): Record<string, number> {
    return Object.fromEntries(this.axisLevels);
  }

  // ── Legacy compat ──

  /** @deprecated Use getAxisUpgradeCost(BaseUpgradeAxis.HP) */
  getUpgradeCost(): number {
    return this.getAxisUpgradeCost(BaseUpgradeAxis.HP);
  }

  /** @deprecated Use upgradeAxis(BaseUpgradeAxis.HP) */
  upgrade(): void {
    this.upgradeAxis(BaseUpgradeAxis.HP);
  }

  /**
   * Damage the nearest alive base node to the attacker position.
   * If no attacker position given, damages the first alive node.
   * Armor reduces incoming damage.
   */
  takeDamage(amount: number, attackerX?: number, attackerY?: number): void {
    if (!this.alive) return;
    // Apply armor reduction
    const dmg = amount * ENEMY_DAMAGE_MULTIPLIER * (1 - this.armorReduction);
    const target = this.nearestAliveNode(attackerX, attackerY);
    if (!target) return;
    const wasAlive = target.alive;
    target.applyDamage(dmg);
    // If this node just died, signal enemies to repath toward surviving bases
    // NOTE: never mutate BASE_POSITIONS — it's the map source data reused on retry
    if (wasAlive && !target.alive) {
      EventBus.emit(GameEvents.BASE_NODE_DESTROYED, {
        deadX: target.x, deadY: target.y,
        alivePositions: this.getAlivePositions(),
      });
    }
    // Recompute centroid of alive nodes
    this.updateCentroid();
    // Emit aggregate HP for UI
    EventBus.emit(GameEvents.BASE_DAMAGED, { hp: this.hp, x: target.x, y: target.y });
    // Check if ALL nodes destroyed → game over
    if (!this.alive) {
      EventBus.emit(GameEvents.BASE_DESTROYED);
      EventBus.emit(GameEvents.GAME_OVER, false);
      SoundManager.playMusic('endCredits' as any);
    }
  }

  stun(): void { /* bases can't be stunned */ }
  knockBack(): void { /* bases can't be knocked back */ }
  applyBurn(): void { /* bases can't burn */ }
  getVelocity(): { vx: number; vy: number } { return { vx: 0, vy: 0 }; }

  /** Heal the nearest alive node (used by repair robots) */
  heal(amount: number, fromX?: number, fromY?: number): void {
    const node = this.nearestAliveNode(fromX, fromY);
    if (!node) return;
    node.heal(amount);
    EventBus.emit(GameEvents.BASE_DAMAGED, { hp: this.hp, x: node.x, y: node.y }); // update UI bar
  }

  /** Return all alive nodes as IBaseTarget for projectile/beam targeting */
  getAliveTargets(): IBaseTarget[] {
    return this.nodes.filter(n => n.alive).map(n => ({
      x: n.x, y: n.y, alive: n.alive,
      takeDamage: (d: number) => this.takeDamage(d, n.x, n.y),
      collisionRadius: this.collisionRadius,
      stun: () => {}, knockBack: () => {}, applyBurn: () => {},
      getVelocity: () => ({ vx: 0, vy: 0 }),
    }));
  }

  /** Nearest alive base position (for enemy shooting target selection) */
  nearestAlivePosition(fromX: number, fromY: number): { x: number; y: number } {
    let best: Actor | null = null;
    let bestDist = Infinity;
    for (const n of this.nodes) {
      if (!n.alive) continue;
      const d = (n.x - fromX) ** 2 + (n.y - fromY) ** 2;
      if (d < bestDist) { bestDist = d; best = n; }
    }
    return best ? { x: best.x, y: best.y } : { x: this.x, y: this.y };
  }

  /** Return positions of all alive base nodes */
  getAlivePositions(): { x: number; y: number }[] {
    return this.nodes.filter(n => n.alive).map(n => ({ x: n.x, y: n.y }));
  }

  /** Check if a world point is close to any alive base node */
  isNearAnyNode(px: number, py: number, radius: number): boolean {
    for (const node of this.nodes) {
      if (!node.alive) continue;
      const dx = px - node.x;
      const dy = py - node.y;
      if (dx * dx + dy * dy < radius * radius) return true;
    }
    return false;
  }

  /** Call each frame during WAVE phase. Delegates to each Actor's turret.
   *  Passes upgrade-derived stats for range/damage/cannon count. */
  updateTurret(dt: number, enemies: TurretTarget[]): void {
    if (!this.alive) return;
    for (const node of this.nodes) {
      node.updateTurret(dt, enemies, {
        bonusRangeTiles: this.bonusRangeTiles,
        bonusDamage: this.bonusDamage,
        totalCannons: this.totalCannons,
      });
    }
  }

  private nearestAliveNode(ax?: number, ay?: number): Actor | null {
    const aliveNodes = this.nodes.filter(n => n.alive);
    if (aliveNodes.length === 0) return null;
    if (ax === undefined || ay === undefined) return aliveNodes[0];
    let best = aliveNodes[0];
    let bestDist = (best.x - ax) ** 2 + (best.y - ay) ** 2;
    for (let i = 1; i < aliveNodes.length; i++) {
      const d = (aliveNodes[i].x - ax) ** 2 + (aliveNodes[i].y - ay) ** 2;
      if (d < bestDist) { bestDist = d; best = aliveNodes[i]; }
    }
    return best;
  }

  private updateCentroid(): void {
    const alive = this.nodes.filter(n => n.alive);
    if (alive.length === 0) return;
    this.x = alive.reduce((s, n) => s + n.x, 0) / alive.length;
    this.y = alive.reduce((s, n) => s + n.y, 0) / alive.length;
  }

  destroy(): void {
    for (const n of this.nodes) n.destroy();
    this.nodes = [];
  }
}
